import { LuEllipsis, LuPlus } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageTitle, PageTitleRow } from '@/components/ui/page-layout';
import { useAuthContext } from 'views/auth/Auth';
import { GlobalSearchTermInput } from 'widgets/form/filter';

import { InvestmentRequestMultiFilter } from './InvestmentRequestMultiFilter';

interface RunOptimizationButtonProps {
    selectedRequestCount: number;
    selectedPortfolioCount: number;
    selectedPortfolioGuids: string[];
}

const RunOptimizationButton = ({
    selectedRequestCount,
    selectedPortfolioCount,
    selectedPortfolioGuids,
}: RunOptimizationButtonProps) => {
    if (selectedPortfolioCount === 0) return null;

    const handleRunOptimization = () => {
        const params = new URLSearchParams({ portfolioGuids: selectedPortfolioGuids.join(',') });
        window.open(`/optimizer/builder?${params.toString()}`, '_blank');
    };

    const buttonText =
        selectedRequestCount === selectedPortfolioCount
            ? `Run Optimization (${selectedPortfolioCount})`
            : `Run Optimization (${selectedPortfolioCount} portfolios from ${selectedRequestCount} requests)`;

    return (
        <Button onClick={handleRunOptimization} variant="outline2" size="sm">
            {buttonText}
        </Button>
    );
};

interface InvestmentRequestFilterBarProps {
    count: number;
    onExportCsv: () => void;
    selectedRequestCount: number;
    selectedPortfolioCount: number;
    selectedPortfolioGuids: string[];
}

export const InvestmentRequestFilterBar = ({
    count,
    onExportCsv,
    selectedRequestCount,
    selectedPortfolioCount,
    selectedPortfolioGuids,
}: InvestmentRequestFilterBarProps) => {
    const { selectedTenants } = useAuthContext();

    return (
        <PageTitleRow
            actions={
                <div className="flex items-center gap-2">
                    <GlobalSearchTermInput placeholder="Search investment r..." round />
                    <InvestmentRequestMultiFilter />
                    <RunOptimizationButton
                        selectedRequestCount={selectedRequestCount}
                        selectedPortfolioCount={selectedPortfolioCount}
                        selectedPortfolioGuids={selectedPortfolioGuids}
                    />
                    <Link to="create">
                        <Button variant="outline2" size="sm">
                            <LuPlus className="h-4 w-4" /> Investment Request
                        </Button>
                    </Link>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline2" size="sm" className="px-2">
                                <LuEllipsis className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={onExportCsv}>Export .csv</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            }
            className="gap-4"
        >
            <div>
                <PageTitle>Investment Requests</PageTitle>
                <p className="text-cocoa-600 mt-0.5 text-sm font-thin">
                    {count} requests · {selectedTenants.length} tenants
                </p>
            </div>
        </PageTitleRow>
    );
};
